export const REGION_STORAGE_PREFIX = `regions:company`;
export const REGION_UPDATED_EVENT = 'regions:updated';

function safeJsonParse(value, fallback = []) {
    try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : fallback;
    } catch (_error) {
        return fallback;
    }
}

// Normalizes an input array to unique, trimmed, non-empty string values.
function uniqueStringArray(values) {
    if (!Array.isArray(values)) {
        return [];
    }

    return [...new Set(values.map((value) => String(value).trim()))].filter(
        (value) => value.length > 0
    );
}

/**
 * Builds the localStorage key for region data per company.
 *
 * @param {string|number} companyId - Company identifier.
 * @returns {string} Company-scoped localStorage key.
 */
export function getRegionStorageKey(companyId) {
    return `${REGION_STORAGE_PREFIX}:${String(companyId || 'unknown')}`;
}

/**
 * Resolves site IDs that belong to any of the selected provinces/states.
 *
 * @param {string[]} provinceIds - Selected province/state IDs.
 * @param {Array<Object>} sites - Raw site records.
 * @returns {string[]} Matched site IDs.
 */
export function getSiteIdsByProvinceIds(provinceIds = [], sites = []) {
    const provinceSet = new Set(uniqueStringArray(provinceIds));

    if (provinceSet.size === 0) {
        return [];
    }

    return sites
        .filter((site) => provinceSet.has(String(site?.province || '')))
        .map((site) => String(site.id));
}

/**
 * Normalizes a region object into the canonical local shape.
 *
 * @param {Object} region - Region input.
 * @returns {{id: string, name: string, assignmentMode: 'sites'|'province', provinceIds: string[], siteIds: string[], createdAt: string, updatedAt: string}} Normalized region.
 */
export function normalizeRegion(region = {}) {
    const assignmentMode =
        region?.assignmentMode === 'province' ? 'province' : 'sites';

    return {
        id: String(region?.id || ''),
        name: String(region?.name || '').trim(),
        assignmentMode,
        provinceIds: uniqueStringArray(region?.provinceIds),
        siteIds: uniqueStringArray(region?.siteIds),
        createdAt: String(region?.createdAt || '').trim(),
        updatedAt: String(region?.updatedAt || '').trim(),
    };
}

/**
 * Normalizes a typed global region selection into a safe persisted shape.
 *
 * @param {Object|null} region - Candidate global region selection.
 * @returns {{kind: 'province', province: string, label: string}|{kind: 'customRegion', regionId: string, label: string}|null} Normalized selection.
 */
export function normalizeGlobalRegionSelection(region = null) {
    if (!region || typeof region !== 'object') {
        return null;
    }

    if (region.kind === 'province') {
        const province = String(region.province || '').trim();
        if (!province) {
            return null;
        }

        return {
            kind: 'province',
            province,
            label: String(region.label || province).trim() || province,
        };
    }

    if (region.kind === 'customRegion') {
        const regionId = String(region.regionId || '').trim();
        if (!regionId) {
            return null;
        }

        return {
            kind: 'customRegion',
            regionId,
            label: String(region.label || regionId).trim() || regionId,
        };
    }

    return null;
}

/**
 * Normalizes an array of region objects.
 *
 * @param {Array<Object>} regions - Region list.
 * @returns {Array<Object>} Normalized regions.
 */
export function normalizeRegions(regions = []) {
    return (Array.isArray(regions) ? regions : []).map((region) =>
        normalizeRegion(region)
    );
}

/**
 * Syncs region-site relationships against the current site list.
 * - Province mode: recalculates siteIds from provinceIds.
 * - Sites mode: prunes siteIds that no longer exist.
 *
 * @param {Array<Object>} regions - Region list.
 * @param {Array<Object>} sites - Current site list.
 * @returns {Array<Object>} Synced regions.
 */
export function syncRegionsWithSites(regions = [], sites = []) {
    const validSiteIds = new Set(
        sites.map((site) => String(site?.id)).filter((siteId) => siteId !== '')
    );

    return normalizeRegions(regions).map((region) => {
        const synced = { ...region };

        if (synced.assignmentMode === 'province') {
            synced.siteIds = getSiteIdsByProvinceIds(synced.provinceIds, sites);
            return synced;
        }

        synced.siteIds = synced.siteIds.filter((siteId) =>
            validSiteIds.has(siteId)
        );
        return synced;
    });
}

/**
 * Loads regions from localStorage for a specific company and syncs them with sites.
 *
 * @param {string|number} companyId - Company identifier.
 * @param {Array<Object>} sites - Current site list.
 * @returns {Array<Object>} Loaded and synced regions.
 */
export function loadRegions(companyId, sites = []) {
    if (!companyId) {
        return [];
    }

    const regionStorageKey = getRegionStorageKey(companyId);
    const storedRegions = safeJsonParse(
        localStorage.getItem(regionStorageKey),
        []
    );
    return syncRegionsWithSites(storedRegions, sites);
}

/**
 * Persists normalized regions to localStorage for a specific company.
 *
 * @param {string|number} companyId - Company identifier.
 * @param {Array<Object>} regions - Region list to save.
 * @returns {void}
 */
export function saveRegions(companyId, regions = []) {
    if (!companyId) {
        return;
    }

    const regionStorageKey = getRegionStorageKey(companyId);
    localStorage.setItem(
        regionStorageKey,
        JSON.stringify(normalizeRegions(regions))
    );

    if (typeof window !== 'undefined' && window?.dispatchEvent) {
        window.dispatchEvent(
            new CustomEvent(REGION_UPDATED_EVENT, {
                detail: {
                    companyId: String(companyId),
                },
            })
        );
    }
}

/**
 * Creates a new persisted region record with generated ID/timestamps.
 *
 * @param {Object} regionInput - Region input from form.
 * @returns {Object} New region record.
 */
export function createRegionRecord(regionInput = {}) {
    const now = new Date().toISOString();
    const normalized = normalizeRegion(regionInput);

    return {
        ...normalized,
        id:
            normalized.id ||
            `reg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        createdAt: normalized.createdAt || now,
        updatedAt: now,
    };
}

/**
 * Converts regions to Field-compatible option objects.
 *
 * @param {Array<Object>} regions - Region list.
 * @returns {Array<{text: string, value: string}>} Sorted region options.
 */
export function getRegionOptions(regions = []) {
    return normalizeRegions(regions)
        .map((region) => ({
            text: region.name,
            value: String(region.id),
        }))
        .sort((a, b) => a.text.localeCompare(b.text));
}

/**
 * Encodes a typed global region selection into a primitive string value
 * suitable for use in `<Field type="select">` v-model bindings.
 *
 * @param {Object|null} selection - Typed region selection object.
 * @param {'province'|'customRegion'} selection.kind - Selection type.
 * @param {string} [selection.province] - Province code (when kind is 'province').
 * @param {string} [selection.regionId] - Custom region ID (when kind is 'customRegion').
 * @returns {string|null} Encoded string (`"province:<code>"` or `"custom:<id>"`) or `null`.
 */
export function encodeGlobalRegionSelection(selection = null) {
    if (!selection || typeof selection !== 'object') {
        return null;
    }

    if (selection.kind === 'province') {
        const province = String(selection.province || '').trim();
        return province ? `province:${province}` : null;
    }

    if (selection.kind === 'customRegion') {
        const regionId = String(selection.regionId || '').trim();
        return regionId ? `custom:${regionId}` : null;
    }

    return null;
}

/**
 * Decodes a primitive select value back into a typed global region selection object.
 * Returns `null` when the value is empty or does not match a known region/province.
 *
 * @param {string|null} value - Encoded select value (`"province:<code>"` or `"custom:<id>"`).
 * @param {Array<Object>} regions - Available custom region records (used for custom region lookup).
 * @param {Array<{value: string, text?: string}>} provinceOptions - Province option list (used for label resolution).
 * @returns {{kind: 'province', province: string, label: string}|{kind: 'customRegion', regionId: string, label: string}|null} Typed selection or `null`.
 */
export function decodeGlobalRegionSelection(
    value,
    regions = [],
    provinceOptions = []
) {
    const normalizedValue = String(value || '').trim();
    if (!normalizedValue) {
        return null;
    }

    if (normalizedValue.startsWith('province:')) {
        const province = normalizedValue.slice('province:'.length).trim();
        if (!province) {
            return null;
        }

        const matchingProvince = (
            Array.isArray(provinceOptions) ? provinceOptions : []
        ).find((option) => String(option?.value || '').trim() === province);

        return {
            kind: 'province',
            province,
            label: String(matchingProvince?.text || '').trim() || province,
        };
    }

    if (normalizedValue.startsWith('custom:')) {
        const regionId = normalizedValue.slice('custom:'.length).trim();
        if (!regionId) {
            return null;
        }

        const region = normalizeRegions(regions).find(
            (item) => String(item.id) === regionId
        );

        if (!region) {
            return null;
        }

        return {
            kind: 'customRegion',
            regionId,
            label: region.name || regionId,
        };
    }

    return null;
}

/**
 * Builds a merged options array for a Region select field.
 * Custom regions appear first (sorted alphabetically), followed by provinces.
 * Each option value is prefixed (`"custom:<id>"` or `"province:<code>"`) so the
 * selection kind can be determined on decode.
 *
 * @param {Array<Object>} regions - Custom region records.
 * @param {Array<{value: string, text?: string}>} provinceOptions - Province option list from the site store.
 * @returns {Array<{text: string, value: string}>} Merged, Field-compatible options.
 */
export function buildRegionProvinceOptions(regions = [], provinceOptions = []) {
    const regionOptions = getRegionOptions(regions).map((option) => ({
        text: option.text,
        value: `custom:${String(option.value)}`,
    }));

    const provinceSelectOptions = (
        Array.isArray(provinceOptions) ? provinceOptions : []
    ).map((option) => {
        return {
            text: option?.text,
            value: `province:${String(option?.value)}`,
        };
    });

    return [...regionOptions, ...provinceSelectOptions];
}

/**
 * Resolves the set of allowed site IDs for a given typed global region selection.
 * - Province kind: delegates to `getSiteIdsByProvinceIds`.
 * - Custom region kind: returns the region's `siteIds` directly.
 * - No selection: returns an empty array.
 *
 * @param {Object|null} selection - Typed region selection (output of `decodeGlobalRegionSelection`).
 * @param {Array<Object>} regions - Custom region records (needed for custom region lookup).
 * @param {Array<Object>} sites - Raw site records (needed for province-based resolution).
 * @returns {string[]} Matching site IDs (as strings).
 */
export function getAllowedSiteIdsByGlobalRegion(
    selection = null,
    regions = [],
    sites = []
) {
    if (!selection || typeof selection !== 'object') {
        return [];
    }

    if (selection.kind === 'province') {
        return getSiteIdsByProvinceIds([selection.province], sites);
    }

    if (selection.kind === 'customRegion') {
        const region = normalizeRegions(regions).find(
            (item) => String(item.id) === String(selection.regionId)
        );

        return Array.isArray(region?.siteIds)
            ? region.siteIds.map((siteId) => String(siteId))
            : [];
    }

    return [];
}

/**
 * Gets region IDs that include a given site ID.
 *
 * @param {Array<Object>} regions - Region list.
 * @param {string|number|null} siteId - Site identifier.
 * @returns {string[]} Region IDs for the site.
 */
export function getRegionIdsForSite(regions = [], siteId = null) {
    if (!siteId) {
        return [];
    }

    const normalizedSiteId = String(siteId);
    return normalizeRegions(regions)
        .filter((region) => region.siteIds.includes(normalizedSiteId))
        .map((region) => String(region.id));
}

/**
 * Validates region payload before create/update.
 *
 * @param {Object} region - Region payload.
 * @returns {{valid: boolean, message: string}} Validation result.
 */
export function validateRegionInput(region = {}) {
    const normalized = normalizeRegion(region);

    if (!normalized.name) {
        return {
            valid: false,
            message: 'Region Name is required',
        };
    }

    if (!Array.isArray(normalized.siteIds) || normalized.siteIds.length === 0) {
        return {
            valid: false,
            message: 'Select at least one Site',
        };
    }

    return {
        valid: true,
        message: '',
    };
}
